import { describeCarriedFacetSelection } from '../catalog-carried-facet-filter'

export interface CarriedFacetBannerProps {
  /** The category page's category name the shopper selected the facet
   * from (e.g. "Linear Shafts") — this page's own `category` URL param. */
  readonly sourceCategory: string
  /** The carried-over `specs[<name>]=<value>` selection, parsed off this
   * page's own URL (same shape `CatalogSeriesPage` reads/writes). */
  readonly selection: Readonly<Record<string, string>>
  readonly matchCount: number
  readonly totalCount: number
  /** Rows on this series with no `specs` field at all — disclosed
   * separately (ADR-0090/0091 "omit, don't fabricate": their absence from
   * `matchCount` is not the same claim as "known not to match"). */
  readonly omittedCount: number
  readonly onClear: () => void
}

/**
 * A separate, labeled, dismissible block for the facet selection carried
 * over from the category page's series-card grid — deliberately NOT merged
 * visually or logically with `CatalogSeriesFilterSidebar` (this page's own
 * dimension-based filter). The two filter concepts read different fields
 * (`specs` here vs. `configSchema` segments there) and must never imply the
 * same guarantee (ADR-0120 D4's false-equivalence warning) — a shopper
 * clearing one must never look like it also cleared the other.
 *
 * Handles both dishonest-looking-but-honest edge cases explicitly rather
 * than leaving them to look like bugs:
 * - An invariant carried facet (e.g. `Metric/Inch` on a series that's
 *   entirely one value) is a real no-op filter — `matchCount === totalCount`
 *   renders as "All N items match", not a suspicious no-narrowing filter.
 * - Rows with no `specs` on file (`omittedCount > 0`) are surfaced as their
 *   own sentence, not folded into "doesn't match" or silently dropped.
 */
export function CarriedFacetBanner({
  sourceCategory,
  selection,
  matchCount,
  totalCount,
  omittedCount,
  onClear,
}: CarriedFacetBannerProps) {
  const description = describeCarriedFacetSelection(selection)
  const matchesEverything = totalCount > 0 && matchCount === totalCount && omittedCount === 0

  return (
    <section className="catalog-carried-facet-banner" aria-label="Filtered from category selection">
      <div className="catalog-carried-facet-banner-body">
        <p className="catalog-carried-facet-banner-text">
          Filtered from {sourceCategory}: {description}
        </p>
        <p className="catalog-carried-facet-banner-detail">
          {matchesEverything
            ? `All ${totalCount} item${totalCount === 1 ? '' : 's'} match`
            : `Showing ${matchCount} of ${totalCount} item${totalCount === 1 ? '' : 's'}`}
          {omittedCount > 0 &&
            ` · ${omittedCount} item${omittedCount === 1 ? '' : 's'} with no spec data on file`}
        </p>
      </div>
      <button type="button" className="catalog-carried-facet-banner-clear" onClick={onClear}>
        Clear filter from {sourceCategory}
      </button>
    </section>
  )
}
